import { useState, useContext, useEffect, useRef } from "react";
import { Container, Row, Alert } from "react-bootstrap";
import { FaClock, FaMapMarkerAlt, FaStar, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/Drivers/DriverDashboard/DriverDashboard.css";
import StatCard from "../../../components/Drivers/DashboardStats/StatCard";
import DriverMasterLayout from "../../../layouts/Drivers/DriverMasterLayout";
import axios from "axios";
import AppContext from "../../../context/AppContext";

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [bidAmounts, setBidAmounts] = useState({});
  const [bidSubmitted, setBidSubmitted] = useState({});
  const [upcomingRides, setUpcomingRides] = useState([]);
  const [isVerified, setIsVerified] = useState(null); 
  const [showBlockedAttention, setShowBlockedAttention] = useState(false);
  const { baseUrl, authToken } = useContext(AppContext);
  const verifyAlertRef = useRef(null);

  const [stats, setStats] = useState({
    totalEarnings: 0,
    completeRide: 0,
    pendingRide: 0,
    cancelRide: 0,
  });

  const getDriverDashboardStats = () => {
    axios
      .get(`${baseUrl}/driver/ride/driverDashboard`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          if (response.data.driver?.is_verified) {
            setIsVerified(response.data.driver.is_verified);
          }
          if (response.data.stats) {
            setStats(response.data.stats);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleBidChange = (rideId, value) => {
    setBidAmounts((prev) => ({ ...prev, [rideId]: value }));
  };

  const triggerBlockedAttention = () => {
    setShowBlockedAttention(true);
    verifyAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => setShowBlockedAttention(false), 2500);
  };

  const handleBidSubmit = (ride) => {
    // Block ride acceptance until admin has verified documents
    if (isVerified !== "verified") {
      triggerBlockedAttention();
      return;
    }

    const amount = bidAmounts[ride.id];
    if (!amount || isNaN(amount) || Number(amount) <= 0) return;

    const formData = new FormData();
    formData.append("tdride_id", ride.id);
    formData.append("bidAmounts", amount);

    axios
      .post(`${baseUrl}/driver/ride/createbid`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success === true) {
          setBidSubmitted((prev) => ({ ...prev, [ride.id]: amount }));
        } else if (response.data.message) {
          triggerBlockedAttention();
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.status === 403) {
          triggerBlockedAttention();
        } else {
          alert("Network Error! Please try again.");
        }
      });
  };

  const handleAcceptCrossbid = (ride) => {
    // Block ride acceptance until admin has verified documents
    if (isVerified !== "verified") {
      triggerBlockedAttention();
      return;
    }

    const amount = ride.crossbidprice;
    if (!amount) return;

    const formData = new FormData();
    formData.append("tdride_id", ride.id);
    formData.append("bidAmounts", amount);

    axios
      .post(`${baseUrl}/driver/ride/createbid`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success === true) {
          setBidSubmitted((prev) => ({ ...prev, [ride.id]: amount }));
        } else if (response.data.message) {
          triggerBlockedAttention();
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.status === 403) {
          triggerBlockedAttention();
        } else {
          alert("Network Error! Please try again.");
        }
      });
  };

  const handleTotalEarningsClick = () => navigate("/driver-mywallet");
  const handleCompleteRidesClick = () => navigate("/driver-myrides");
  const handlePendingRidesClick = () => navigate("/driver-active-rides");
  const handleCancelRidesClick = () => navigate("/driver-myrides");

  const fetchupcomingrides = (status) => {
    const formData = new FormData();
    formData.append("status", status);

    axios
      .post(`${baseUrl}/driver/ride/viewupcomingRides`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        console.log("Upcoming rides Rides Response:", response.data);
        if (response.data.success === true) {
          setUpcomingRides(response.data.rides);
        }
      })
      .catch((error) => {
        console.log(error);
        alert("Network Error! Please try again.");
      });
  };

  useEffect(() => {
    getDriverDashboardStats();
    fetchupcomingrides("request");
  }, []);

  return (
    <DriverMasterLayout screenName={"TrustiDrive"} centered={false}>
      <div className="driver-dashboard">
        <Container fluid className="driver-dashboard__main">
          {/* Verification Banner */}
          {isVerified && isVerified !== "verified" && (
            <Alert
              ref={verifyAlertRef}
              variant="warning"
              className={`driver-dashboard__verify-alert${
                showBlockedAttention ? " driver-dashboard__verify-alert--attention" : ""
              }`}
            >
              {isVerified === "pending" &&
                "Your documents are under review. You will be able to accept rides once approved."}
              {isVerified === "rejected" &&
                "Your documents were rejected. Please resubmit correct documents to start accepting rides."}
            </Alert>
          )}

          {/* Stats Cards */}
          <Row className="driver-dashboard__stats">
            <StatCard
              totalEarnings={stats.totalEarnings}
              label={"Total Earning"}
              onClick={handleTotalEarningsClick}
            />
            <StatCard
              totalEarnings={stats.completeRide}
              label={"Complete Rides"}
              onClick={handleCompleteRidesClick}
            />
            <StatCard
              totalEarnings={stats.pendingRide}
              label={"Pending Rides"}
              onClick={handlePendingRidesClick}
            />
            <StatCard
              totalEarnings={stats.cancelRide}
              label={"Cancelled Rides"}
              onClick={handleCancelRidesClick}
            />
          </Row>

          {/* Upcoming Rides */}
          <div className="driver-dashboard__section">
            <h3 className="driver-dashboard__section-title">
              Now Upcoming Ride
            </h3>
            {upcomingRides.length === 0 && (
              <div className="driver-dashboard__empty-state">
                <div className="driver-dashboard__empty-icon">🚗</div>
                <h5 className="driver-dashboard__empty-title">No Upcoming Rides</h5>
                <p className="driver-dashboard__empty-subtitle">New ride requests will appear here</p>
              </div>
            )}
            {upcomingRides.map((ride) => (
              <div key={ride.id} className="driver-dashboard__ride-card">
                {/* Header Row */}
                <div className="driver-dashboard__ride-header">
                  <div className="driver-dashboard__rider-info">
                    <div className="driver-dashboard__rider-avatar">
                      <FaUser size={12} />
                    </div>
                    <span className="driver-dashboard__rider-name">
                      {ride.tdcustomer?.name}
                    </span>
                  </div>
                  <div className="driver-dashboard__ride-meta">
                    <span className="driver-dashboard__rating">
                      <FaStar size={10} /> {ride.rating}
                    </span>
                    <span className="driver-dashboard__fare">
                      {ride.offeredFare}
                    </span>
                  </div>
                </div>

                {/* Date/Time and Distance Row */}
                <div className="driver-dashboard__ride-info-row">
                  <div className="driver-dashboard__ride-time">
                    <FaClock size={11} />
                    <span>
                      {ride.bookingDate} at {ride.bookingTime}
                    </span>
                  </div>
                  <div className="driver-dashboard__ride-distance">
                    <FaMapMarkerAlt size={11} />
                    <span>{ride.distance}</span>
                  </div>
                </div>

                {/* Locations */}
                <div className="driver-dashboard__ride-locations">
                  <div className="driver-dashboard__location-row">
                    <div className="driver-dashboard__location-icon driver-dashboard__location-icon--pickup">
                      <FaMapMarkerAlt size={10} />
                    </div>
                    <div className="driver-dashboard__location-text">
                      <span className="driver-dashboard__location-label">
                        Pickup
                      </span>
                      <span className="driver-dashboard__location-address">
                        {ride.pickuplocation}
                      </span>
                    </div>
                  </div>
                  <div className="driver-dashboard__location-row">
                    <div className="driver-dashboard__location-icon driver-dashboard__location-icon--drop">
                      <FaMapMarkerAlt size={10} />
                    </div>
                    <div className="driver-dashboard__location-text">
                      <span className="driver-dashboard__location-label">
                        Drop
                      </span>
                      <span className="driver-dashboard__location-address">
                        {ride.dropofflocation}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bid Section — always visible */}
                <div className="driver-dashboard__bid-section">
                  {bidSubmitted[ride.id] ? (
                    <div className="driver-dashboard__bid-success">
                      <span className="driver-dashboard__bid-success-icon">
                        ✓
                      </span>
                      <div>
                        <p className="driver-dashboard__bid-success-title">
                          Bid Submitted!
                        </p>
                        <p className="driver-dashboard__bid-success-amount">
                          Your bid: <strong>Rs.{bidSubmitted[ride.id]}</strong>
                        </p>
                        <p className="driver-dashboard__bid-success-note">
                          Waiting for customer to accept...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {ride.iscrossbidded && (
                        <div className="driver-dashboard__crossbid-alert">
                          <p className="driver-dashboard__crossbid-text">
                            Customer offered <strong>Rs.{ride.crossbidprice}</strong>
                          </p>
                          <button
                            className="driver-dashboard__crossbid-accept-btn"
                            onClick={() => handleAcceptCrossbid(ride)}
                          >
                            Accept Rs.{ride.crossbidprice}
                          </button>
                        </div>
                      )}
                      <p className="driver-dashboard__bid-label">
                        Enter your bid amount
                      </p>
                      <div className="driver-dashboard__bid-row">
                        <div className="driver-dashboard__bid-input-wrapper">
                          <span className="driver-dashboard__bid-currency">
                            Rs
                          </span>
                          <input
                            type="number"
                            className="driver-dashboard__bid-input"
                            placeholder="e.g. 200"
                            value={bidAmounts[ride.id] || ""}
                            onChange={(e) =>
                              handleBidChange(ride.id, e.target.value)
                            }
                            min="1"
                          />
                        </div>
                        <button
                          className="driver-dashboard__bid-btn"
                          onClick={() => handleBidSubmit(ride)}
                          disabled={
                            !bidAmounts[ride.id] ||
                            Number(bidAmounts[ride.id]) <= 0
                          }
                        >
                          Submit Bid
                        </button>
                      </div>
                      <p className="driver-dashboard__bid-hint">
                        Suggested fare: {ride.fare} &nbsp;·&nbsp;{" "}
                        {ride.distance}
                      </p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </div>
    </DriverMasterLayout>
  );
};

export default DriverDashboard;